import { useState, useEffect, useCallback } from 'react'
import { Edit2, ExternalLink, X, Save, Archive, Trash2, RotateCcw, AlertTriangle } from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { DONE_STATUSES } from './constants'
import { StartOffboardingModal, ArchiveClientModal, checklistProgress } from './offboarding'
import { logClientChanges } from './clientChangeLog'

export default function ClientProfile({ client, profiles, tasks, currentUser, onClose, onUpdated, onOpenFull }) {
  const toast = useToast()
  const [editing, setEditing] = useState(false)
  const [confirmAction, setConfirmAction] = useState(null) // 'unarchive' | 'delete'
  // Archiving goes through ArchiveClientModal, which checks offboarding first.
  const [showArchive, setShowArchive] = useState(false)
  const [showStartOffboarding, setShowStartOffboarding] = useState(false)
  const [form, setForm] = useState({
    name: client.name || '',
    package_label: client.package_label || '',
    website: client.website || '',
    hosting_platform: client.hosting_platform || '',
    company_location: client.company_location || '',
    primary_contact_name: client.primary_contact_name || '',
    primary_contact_position: client.primary_contact_position || '',
    primary_contact_cell: client.primary_contact_cell || '',
    contact_email: client.contact_email || '',
    drive_link: client.drive_link || '',
    notes: client.notes || '',
    account_director_id: client.account_director_id || '',
    account_manager_id: client.account_manager_id || '',
    is_onboarding: client.is_onboarding || false,
    onboarding_complete: client.onboarding_complete || false,
    start_date: client.start_date || '',
    terminated_on: client.terminated_on || '',
    // Service flags
    has_website: client.has_website || false,
    has_ads: client.has_ads || false,
    has_seo: client.has_seo || false,
    has_social: client.has_social || false,
    has_newsletters: client.has_newsletters || false,
    has_blogs: client.has_blogs || false,
    // Quantities
    social_posts_per_month: client.social_posts_per_month ?? '',
    blogs_per_month: client.blogs_per_month ?? '',
    newsletters_per_month: client.newsletters_per_month ?? '',
  })
  const [saving, setSaving] = useState(false)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const clientTasks = tasks.filter(t => t.client_id === client.id)
  const openTasks = clientTasks.filter(t => !['approved', 'live'].includes(t.status))
  const doneTasks = clientTasks.filter(t => ['approved', 'live'].includes(t.status))
  const overdueTasks = clientTasks.filter(t => t.date_due && new Date(t.date_due) < new Date() && !['approved', 'live'].includes(t.status))

  const ad = profiles.find(p => p.id === client.account_director_id)
  const am = profiles.find(p => p.id === client.account_manager_id)
  const isArchived = !!client.archived_at
  // Open offboarding checklists for this client, if offboarding has started.
  const offboardingTasks = clientTasks.filter(t => t.offboarding)
  const offboardingOpen = offboardingTasks.some(t => !DONE_STATUSES.includes(t.status))

  const save = async () => {
    setSaving(true)
    // Convert empty quantity strings to null for the int columns
    const payload = {
      ...form,
      account_director_id: form.account_director_id || null,
      account_manager_id: form.account_manager_id || null,
      social_posts_per_month: form.social_posts_per_month === '' ? null : parseInt(form.social_posts_per_month, 10),
      blogs_per_month: form.blogs_per_month === '' ? null : parseInt(form.blogs_per_month, 10),
      newsletters_per_month: form.newsletters_per_month === '' ? null : parseInt(form.newsletters_per_month, 10),
      terminated_on: form.terminated_on || null,
    }
    // Only archived clients have a termination date to edit.
    if (!isArchived) delete payload.terminated_on
    const { error } = await supabase.from('clients').update(payload).eq('id', client.id)
    if (error) { toast('Error: ' + error.message, 'error'); setSaving(false); return }
    // Log inventory-relevant changes (fire and forget)
    logClientChanges(client.id, client, payload, currentUser?.id)
    toast('Client profile updated', 'success')
    setEditing(false)
    onUpdated()
    setSaving(false)
  }

  const unarchiveClient = async () => {
    setSaving(true)
    const { error } = await supabase.from('clients').update({ archived_at: null, terminated_on: null }).eq('id', client.id)
    if (error) { toast('Error: ' + error.message, 'error'); setSaving(false); return }
    // Cascade: bring every task on this client back into active views.
    const { error: tErr } = await supabase.from('tasks').update({ archived_at: null }).eq('client_id', client.id).not('archived_at', 'is', null)
    if (tErr) { toast('Client unarchived, but task cascade failed: ' + tErr.message, 'error'); setSaving(false); return }
    toast(`${client.name} unarchived`, 'success')
    setConfirmAction(null)
    setSaving(false)
    onUpdated()
  }

  const deleteClient = async () => {
    setSaving(true)
    try {
      // Cascade delete: tasks first (and related task_comments will cascade via FK if set, else best-effort)
      await supabase.from('task_comments').delete().in('task_id',
        (await supabase.from('tasks').select('id').eq('client_id', client.id)).data?.map(t => t.id) || []
      )
      await supabase.from('tasks').delete().eq('client_id', client.id)
      // Delete kickoffs (cascades via FK on kickoffs.client_id)
      await supabase.from('kickoffs').delete().eq('client_id', client.id)
      // Delete meeting notes if any
      await supabase.from('meeting_notes').delete().eq('client_id', client.id)
      // Delete blog assignments
      await supabase.from('blog_assignments').delete().eq('client_id', client.id)
      // Finally the client
      const { error } = await supabase.from('clients').delete().eq('id', client.id)
      if (error) throw error
      toast(`${client.name} and all related data deleted`, 'success')
      setConfirmAction(null)
      onClose()
      onUpdated()
    } catch (e) {
      toast('Error deleting: ' + e.message, 'error')
    } finally {
      setSaving(false)
    }
  }

  // Show all active profiles in both dropdowns. Small team, no need for role gating.
  const adProfiles = profiles
    .filter(p => !p.archived_at)
    .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
  const amProfiles = adProfiles

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 90, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(26,43,74,0.3)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: 520, background: '#fff', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>

        {/* Header */}
        <div style={{ background: isArchived ? '#5a6478' : 'var(--navy)', padding: '24px 24px 20px', color: '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              {editing ? (
                <input value={form.name} onChange={e => set('name', e.target.value)}
                  style={{ fontFamily: 'var(--font-display)', fontSize: 22, background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.4)', color: '#fff', outline: 'none', width: '100%', marginBottom: 8 }} />
              ) : (
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, marginBottom: 6 }}>{client.name}</h2>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {isArchived && (
                  <span style={{ fontSize: 11, background: 'rgba(255,255,255,0.2)', padding: '2px 10px', borderRadius: 50, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Archive size={11} /> Archived{client.terminated_on && ` · terminated ${new Date(client.terminated_on + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                  </span>
                )}
                {client.is_onboarding && !client.onboarding_complete && (
                  <span style={{ fontSize: 11, background: 'var(--mauve)', padding: '2px 10px', borderRadius: 50, fontWeight: 600 }}>Onboarding</span>
                )}
                {form.package_label && !editing && (
                  <span style={{ fontSize: 11, background: 'rgba(255,255,255,0.15)', padding: '2px 10px', borderRadius: 50 }}>{form.package_label}</span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {!editing && onOpenFull && (
                <button onClick={onOpenFull} title="Open full profile"
                  style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', padding: '5px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontWeight: 500 }}>
                  📋 Full Profile
                </button>
              )}
              {!editing && <button className="btn-icon" style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff' }} onClick={() => setEditing(true)}><Edit2 size={14} /></button>}
              {editing && <button className="btn btn-sm" style={{ background: 'var(--mauve)', color: '#fff', border: 'none' }} onClick={save} disabled={saving}><Save size={13} />{saving ? 'Saving...' : 'Save'}</button>}
              {editing && <button className="btn-icon" style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff' }} onClick={() => setEditing(false)}><X size={14} /></button>}
              {!editing && <button className="btn-icon" style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff' }} onClick={onClose}><X size={14} /></button>}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', borderBottom: '1px solid var(--border-light)' }}>
          {[
            { label: 'Open Tasks', value: openTasks.length, color: 'var(--navy)' },
            { label: 'Completed', value: doneTasks.length, color: '#2e6e3a' },
            { label: 'Overdue', value: overdueTasks.length, color: overdueTasks.length > 0 ? 'var(--red)' : 'var(--text-muted)' },
          ].map(s => (
            <div key={s.label} style={{ padding: '16px', textAlign: 'center', borderRight: '1px solid var(--border-light)' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 26, color: s.color, fontWeight: 700 }}>{s.value}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: '20px 24px', flex: 1 }}>

          {/* Team */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Account Team</div>
            <div className="form-row">
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Account Director</div>
                {editing ? (
                  <select className="form-control" value={form.account_director_id} onChange={e => set('account_director_id', e.target.value)}>
                    <option value="">Not assigned</option>
                    {adProfiles.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                  </select>
                ) : (
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{ad?.full_name || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Not assigned</span>}</div>
                )}
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Account Manager</div>
                {editing ? (
                  <select className="form-control" value={form.account_manager_id} onChange={e => set('account_manager_id', e.target.value)}>
                    <option value="">Not assigned</option>
                    {amProfiles.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                  </select>
                ) : (
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{am?.full_name || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Not assigned</span>}</div>
                )}
              </div>
            </div>
          </div>

          {/* Services */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Services</div>
            {editing ? (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 10 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.has_website} onChange={e => set('has_website', e.target.checked)} /> 🌐 Website
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.has_ads} onChange={e => set('has_ads', e.target.checked)} /> 📣 Ads
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.has_seo} onChange={e => set('has_seo', e.target.checked)} /> 🔍 SEO
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.has_social} onChange={e => set('has_social', e.target.checked)} /> 📱 Social
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.has_newsletters} onChange={e => set('has_newsletters', e.target.checked)} /> 📧 Newsletters
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.has_blogs} onChange={e => set('has_blogs', e.target.checked)} /> ✍️ Blogs
                  </label>
                </div>
                {/* Quantities — only show for active services */}
                <div className="form-row">
                  {form.has_social && (
                    <div className="form-group">
                      <label className="form-label">Social posts/month</label>
                      <input className="form-control" type="number" min="0" value={form.social_posts_per_month} onChange={e => set('social_posts_per_month', e.target.value)} placeholder="10" />
                    </div>
                  )}
                  {form.has_blogs && (
                    <div className="form-group">
                      <label className="form-label">Blogs/month</label>
                      <input className="form-control" type="number" min="0" value={form.blogs_per_month} onChange={e => set('blogs_per_month', e.target.value)} placeholder="2" />
                    </div>
                  )}
                  {form.has_newsletters && (
                    <div className="form-group">
                      <label className="form-label">Newsletters/month</label>
                      <input className="form-control" type="number" min="0" value={form.newsletters_per_month} onChange={e => set('newsletters_per_month', e.target.value)} placeholder="2" />
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {[
                  form.has_website && '🌐 Website',
                  form.has_ads && '📣 Ads',
                  form.has_seo && '🔍 SEO',
                  form.has_social && `📱 Social${form.social_posts_per_month ? ` (${form.social_posts_per_month}/mo)` : ''}`,
                  form.has_newsletters && `📧 Newsletters${form.newsletters_per_month ? ` (${form.newsletters_per_month}/mo)` : ''}`,
                  form.has_blogs && `✍️ Blogs${form.blogs_per_month ? ` (${form.blogs_per_month}/mo)` : ''}`,
                ].filter(Boolean).map((s, i) => (
                  <span key={i} style={{ fontSize: 12, fontWeight: 600, background: 'var(--cream)', padding: '4px 10px', borderRadius: 50, border: '1px solid var(--border-light)' }}>{s}</span>
                ))}
                {![form.has_website, form.has_ads, form.has_seo, form.has_social, form.has_newsletters, form.has_blogs].some(Boolean) && (
                  <span style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>No services set yet</span>
                )}
              </div>
            )}
          </div>

          {/* Business info */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Business Info</div>
            {editing ? (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Package Label (optional)</label>
                    <input className="form-control" value={form.package_label} onChange={e => set('package_label', e.target.value)} placeholder="e.g. MONTHLY MARKETING - SMALL BUSINESS" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Start Date</label>
                    <input className="form-control" type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} />
                  </div>
                </div>
                {isArchived && (
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Termination Date</label>
                      <input className="form-control" type="date" value={form.terminated_on} onChange={e => set('terminated_on', e.target.value)} />
                    </div>
                    <div className="form-group" />
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Website</label>
                    <input className="form-control" value={form.website} onChange={e => set('website', e.target.value)} placeholder="https://..." />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Location</label>
                    <input className="form-control" value={form.company_location} onChange={e => set('company_location', e.target.value)} placeholder="City, State" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Hosting Platform</label>
                    <input className="form-control" value={form.hosting_platform} onChange={e => set('hosting_platform', e.target.value)} placeholder="WP Engine, Bluehost, Squarespace, etc." />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Drive Folder Link</label>
                    <input className="form-control" value={form.drive_link} onChange={e => set('drive_link', e.target.value)} placeholder="Paste folder URL..." />
                  </div>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {form.start_date && <div><span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Start Date: </span><span style={{ fontSize: 13.5, fontWeight: 500 }}>{new Date(form.start_date + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span></div>}
                {form.package_label && <div><span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Package: </span><span style={{ fontSize: 13.5, fontWeight: 500 }}>{form.package_label}</span></div>}
                {form.company_location && <div><span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Location: </span><span style={{ fontSize: 13.5 }}>{form.company_location}</span></div>}
                {form.hosting_platform && <div><span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Hosting: </span><span style={{ fontSize: 13.5 }}>{form.hosting_platform}</span></div>}
                {form.website && <a href={form.website} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 4 }}><ExternalLink size={12} />{form.website}</a>}
                {form.drive_link && <a href={form.drive_link} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" style={{ width: 'fit-content' }}><ExternalLink size={12} /> Open Drive Folder</a>}
                {!form.package_label && !form.company_location && !form.hosting_platform && !form.website && !form.drive_link && (
                  <span style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>No info added yet — click Edit</span>
                )}
              </div>
            )}
          </div>

          {/* Primary Contact */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Primary Contact</div>
            {editing ? (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Name</label>
                    <input className="form-control" value={form.primary_contact_name} onChange={e => set('primary_contact_name', e.target.value)} placeholder="Contact's name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Position</label>
                    <input className="form-control" value={form.primary_contact_position} onChange={e => set('primary_contact_position', e.target.value)} placeholder="e.g. CEO" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input className="form-control" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} placeholder="email@client.com" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cell</label>
                    <input className="form-control" value={form.primary_contact_cell} onChange={e => set('primary_contact_cell', e.target.value)} placeholder="(123) 456-7890" />
                  </div>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {form.primary_contact_name && <div style={{ fontSize: 13.5, fontWeight: 500 }}>{form.primary_contact_name}{form.primary_contact_position && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> · {form.primary_contact_position}</span>}</div>}
                {form.contact_email && <a href={`mailto:${form.contact_email}`} style={{ fontSize: 13, color: 'var(--teal)' }}>{form.contact_email}</a>}
                {form.primary_contact_cell && <div style={{ fontSize: 13 }}>{form.primary_contact_cell}</div>}
                {!form.primary_contact_name && !form.contact_email && !form.primary_contact_cell && (
                  <span style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>No contact info yet</span>
                )}
              </div>
            )}
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Internal Notes</div>
            {editing ? (
              <textarea className="form-control" rows={4} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Internal notes about this client..." />
            ) : (
              <p style={{ fontSize: 13.5, lineHeight: 1.6, color: form.notes ? 'var(--text-primary)' : 'var(--text-muted)', fontStyle: form.notes ? 'normal' : 'italic', whiteSpace: 'pre-wrap' }}>
                {form.notes || 'No notes yet'}
              </p>
            )}
          </div>

          {/* Offboarding */}
          {!editing && !isArchived && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Offboarding</div>
              {offboardingTasks.length === 0 ? (
                <>
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowStartOffboarding(true)}>
                    Start offboarding
                  </button>
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
                    Creates a billing and admin checklist and an Account Director checklist. Archiving is the last step.
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {offboardingTasks.map(t => {
                    const { done, total } = checklistProgress(t)
                    const who = profiles.find(p => p.id === t.assigned_to)?.full_name || 'Unassigned'
                    const finished = DONE_STATUSES.includes(t.status) || (total > 0 && done === total)
                    return (
                      <div key={t.id} style={{ fontSize: 13, display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span>{t.offboarding === 'ad' ? 'Account Director' : 'Billing and admin'} · {who}</span>
                        <span style={{ fontWeight: 600, color: finished ? '#2e6e3a' : 'var(--text-muted)' }}>{finished ? '✓ done' : `${done} / ${total}`}</span>
                      </div>
                    )
                  })}
                  {offboardingOpen && (
                    <button className="btn btn-sm" style={{ background: '#c4962a', color: '#fff', border: 'none', width: 'fit-content', marginTop: 4 }}
                      onClick={() => setShowArchive(true)}>
                      <Archive size={13} /> Finish and archive
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Onboarding toggle */}
          {editing && (
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 24 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={form.is_onboarding} onChange={e => set('is_onboarding', e.target.checked)} />
                Currently onboarding
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={form.onboarding_complete} onChange={e => set('onboarding_complete', e.target.checked)} />
                Onboarding complete
              </label>
            </div>
          )}

          {/* Danger Zone */}
          {editing && (
            <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--border-light)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--red)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={13} /> Danger Zone
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {!isArchived && (
                  <button className="btn btn-sm" style={{ background: '#fff8e1', color: '#8a6000', border: '1.5px solid #f0d98a' }}
                    onClick={() => setShowArchive(true)}>
                    <Archive size={13} /> Archive Client
                  </button>
                )}
                {isArchived && (
                  <button className="btn btn-sm" style={{ background: '#e8f5fa', color: '#2a6e84', border: '1.5px solid #b8dde7' }}
                    onClick={() => setConfirmAction('unarchive')}>
                    <RotateCcw size={13} /> Unarchive Client
                  </button>
                )}
                <button className="btn btn-sm btn-danger" onClick={() => setConfirmAction('delete')}>
                  <Trash2 size={13} /> Delete Permanently
                </button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.5 }}>
                <strong>Archive</strong> hides this client from active views but preserves all data. You can unarchive anytime.<br />
                <strong>Delete</strong> permanently removes the client and ALL related tasks, kickoffs, meeting notes, and blog assignments. This cannot be undone.
              </div>
            </div>
          )}
        </div>

        {/* Confirmation modal */}
        {confirmAction && (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setConfirmAction(null)} style={{ zIndex: 100 }}>
            <div className="modal" style={{ maxWidth: 460 }}>
              <div className="modal-header">
                <span className="modal-title">
                  {confirmAction === 'unarchive' && 'Unarchive Client?'}
                  {confirmAction === 'delete' && '⚠️ Delete Client Permanently?'}
                </span>
                <button className="btn-icon" onClick={() => setConfirmAction(null)}><X size={16} /></button>
              </div>
              <div className="modal-body">
                {confirmAction === 'unarchive' && (
                  <p style={{ fontSize: 13.5, lineHeight: 1.6 }}>
                    Restore <strong>{client.name}</strong> to active client lists?
                  </p>
                )}
                {confirmAction === 'delete' && (
                  <>
                    <div style={{ background: '#fdf0f0', border: '1.5px solid #f0c4c4', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--red)', marginBottom: 6 }}>This will permanently delete:</div>
                      <ul style={{ fontSize: 12.5, color: '#8a3a3a', lineHeight: 1.7, paddingLeft: 18, margin: 0 }}>
                        <li><strong>{client.name}</strong></li>
                        <li>{clientTasks.length} task{clientTasks.length !== 1 ? 's' : ''} (and all comments)</li>
                        <li>All kickoffs, meeting notes, and blog assignments</li>
                      </ul>
                    </div>
                    <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      This cannot be undone. If you might bring this client back later, <strong>Archive</strong> instead.
                    </p>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-ghost" onClick={() => setConfirmAction(null)}>Cancel</button>
                {confirmAction === 'unarchive' && (
                  <button className="btn btn-gold" onClick={unarchiveClient} disabled={saving}>
                    {saving ? 'Unarchiving...' : 'Unarchive'}
                  </button>
                )}
                {confirmAction === 'delete' && (
                  <button className="btn btn-danger" onClick={deleteClient} disabled={saving}>
                    {saving ? 'Deleting...' : 'Yes, Delete Permanently'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {showStartOffboarding && (
          <StartOffboardingModal
            client={client}
            profiles={profiles}
            currentUser={currentUser}
            onClose={() => setShowStartOffboarding(false)}
            onStarted={() => { setShowStartOffboarding(false); onUpdated() }}
          />
        )}
        {showArchive && (
          <ArchiveClientModal
            clientId={client.id}
            onClose={() => setShowArchive(false)}
            onArchived={() => { setShowArchive(false); setEditing(false); onUpdated() }}
          />
        )}
      </div>
    </div>
  )
}